import { useCallback, useEffect, useRef, useState } from "react"

import type { ViewportTransform } from "../coordinates/sourceToViewport"
import { drawShapes } from "./renderShapes"
import type { NormPointTuple, Shape, ShapeGeometry, StrokeWidth, Tool } from "./shapes"
import { clampPoint, hitTestShape, prepareFreehand, translateGeometry } from "./strokes"

/**
 * AnnotationCanvas - the interactive drawing/select surface (D-08, RS-12), built on strokes.ts
 * and renderShapes.ts (05-15). Turns pointer input into shape geometry for the op layer; 05-16
 * wires its onCreate/onMove callbacks to opLog/useRoomSync's submitOp, undo/redo and the toolbar.
 *
 * Canvas-only (T-05-77): shapes are drawn with drawShapes, never rendered as HTML.
 */

const MIN_DRAG_NORM = 0.01
const HIT_TOLERANCE_PX = 10
const HIT_TOLERANCE_PX_TOUCH = 16

type Preview =
  | { kind: "arrow"; pointerId: number; start: NormPointTuple; current: NormPointTuple }
  | { kind: "rect"; pointerId: number; start: NormPointTuple; current: NormPointTuple }
  | { kind: "freehand"; pointerId: number; points: NormPointTuple[] }
  | {
      kind: "move"
      pointerId: number
      shapeId: string
      before: ShapeGeometry
      startSource: NormPointTuple
      after: ShapeGeometry
    }
  | null

export function AnnotationCanvas(p: {
  surface: { transform: ViewportTransform; width: number; height: number }
  shapes: Shape[]
  tool: Tool | "select" | "pointer"
  color: string
  width: StrokeWidth
  interactive: boolean
  canModify: (shape: Shape) => boolean
  selectedShapeId: string | null
  onSelect: (shapeId: string | null) => void
  onCreate: (shape: { tool: Tool; points: NormPointTuple[]; color: string; width: StrokeWidth }) => void
  onMove: (shapeId: string, before: ShapeGeometry, after: ShapeGeometry) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const activePointerIdRef = useRef<number | null>(null)
  const [preview, setPreview] = useState<Preview>(null)

  const isDrawTool = p.tool === "arrow" || p.tool === "rect" || p.tool === "freehand"
  const pointerActive = p.interactive && (isDrawTool || p.tool === "select")

  const previewShapes = useCallback((): Shape[] => {
    if (!preview) {
      return p.shapes
    }
    if (preview.kind === "move") {
      return p.shapes.map((s) => (s.id === preview.shapeId ? { ...s, points: preview.after.points } : s))
    }
    if (preview.kind === "arrow" || preview.kind === "rect") {
      const draft: Shape = {
        id: "__preview__",
        tool: preview.kind,
        authorIdentity: "",
        points: [preview.start, preview.current],
        color: p.color,
        width: p.width,
      }
      return [...p.shapes, draft]
    }
    if (preview.points.length >= 2) {
      const draft: Shape = {
        id: "__preview__",
        tool: "freehand",
        authorIdentity: "",
        points: preview.points,
        color: p.color,
        width: p.width,
      }
      return [...p.shapes, draft]
    }
    return p.shapes
  }, [preview, p.shapes, p.color, p.width])

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) {
      return
    }
    const dpr = window.devicePixelRatio || 1
    const w = Math.max(1, p.surface.width)
    const h = Math.max(1, p.surface.height)
    const targetW = Math.round(w * dpr)
    const targetH = Math.round(h * dpr)
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW
      canvas.height = targetH
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)
    if (p.surface.transform.valid) {
      drawShapes(ctx, previewShapes(), p.surface.transform, p.selectedShapeId)
    }
  }, [p.surface, p.selectedShapeId, previewShapes])

  useEffect(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      redraw()
    })
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [redraw])

  const toSourcePoint = useCallback(
    (e: { clientX: number; clientY: number }, rect: DOMRect): NormPointTuple => {
      const source = p.surface.transform.toSource({ x: e.clientX - rect.left, y: e.clientY - rect.top })
      return clampPoint([source.x, source.y])
    },
    [p.surface.transform],
  )

  const topHit = useCallback(
    (px: { x: number; y: number }, tolerancePx: number): Shape | null => {
      for (let i = p.shapes.length - 1; i >= 0; i--) {
        const candidate = p.shapes[i]
        if (!p.canModify(candidate)) {
          continue
        }
        if (hitTestShape(candidate, px, p.surface.transform, tolerancePx)) {
          return candidate
        }
      }
      return null
    },
    [p],
  )

  const cancelPreview = useCallback(() => {
    const canvas = canvasRef.current
    if (canvas && activePointerIdRef.current !== null) {
      try {
        canvas.releasePointerCapture(activePointerIdRef.current)
      } catch {
        // pointer already released (e.g. it left the element) - nothing to clean up
      }
    }
    activePointerIdRef.current = null
    setPreview(null)
  }, [])

  useEffect(() => {
    if (!preview) {
      return
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        cancelPreview()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [preview, cancelPreview])

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!pointerActive) {
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    e.currentTarget.setPointerCapture(e.pointerId)
    activePointerIdRef.current = e.pointerId

    if (p.tool === "select") {
      const px = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      const tolerance = e.pointerType === "touch" ? HIT_TOLERANCE_PX_TOUCH : HIT_TOLERANCE_PX
      const hit = topHit(px, tolerance)
      p.onSelect(hit ? hit.id : null)
      if (hit) {
        const before: ShapeGeometry = { points: hit.points, color: hit.color, width: hit.width }
        setPreview({
          kind: "move",
          pointerId: e.pointerId,
          shapeId: hit.id,
          before,
          startSource: toSourcePoint(e, rect),
          after: before,
        })
      }
      return
    }

    const start = toSourcePoint(e, rect)
    if (p.tool === "arrow" || p.tool === "rect") {
      setPreview({ kind: p.tool, pointerId: e.pointerId, start, current: start })
    } else if (p.tool === "freehand") {
      setPreview({ kind: "freehand", pointerId: e.pointerId, points: [start] })
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!pointerActive) {
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    const pointerId = e.pointerId
    const sourcePoint = toSourcePoint(e, rect)

    setPreview((prev) => {
      if (!prev || prev.pointerId !== pointerId) {
        return prev
      }
      if (prev.kind === "arrow" || prev.kind === "rect") {
        return { ...prev, current: sourcePoint }
      }
      if (prev.kind === "freehand") {
        return { ...prev, points: [...prev.points, sourcePoint] }
      }
      const dx = sourcePoint[0] - prev.startSource[0]
      const dy = sourcePoint[1] - prev.startSource[1]
      return { ...prev, after: translateGeometry(prev.before, dx, dy) }
    })
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    if (activePointerIdRef.current === e.pointerId) {
      e.currentTarget.releasePointerCapture(e.pointerId)
      activePointerIdRef.current = null
    }
    if (!preview || preview.pointerId !== e.pointerId) {
      setPreview(null)
      return
    }

    if (preview.kind === "arrow" || preview.kind === "rect") {
      const current = toSourcePoint(e, rect)
      const dist = Math.hypot(current[0] - preview.start[0], current[1] - preview.start[1])
      if (dist >= MIN_DRAG_NORM) {
        p.onCreate({ tool: preview.kind, points: [preview.start, current], color: p.color, width: p.width })
      }
    } else if (preview.kind === "freehand") {
      const current = toSourcePoint(e, rect)
      const points = prepareFreehand([...preview.points, current])
      p.onCreate({ tool: "freehand", points, color: p.color, width: p.width })
    } else if (preview.kind === "move") {
      const moved = preview.before.points.some(
        (pt, i) => pt[0] !== preview.after.points[i]?.[0] || pt[1] !== preview.after.points[i]?.[1],
      )
      if (moved) {
        p.onMove(preview.shapeId, preview.before, preview.after)
      }
    }
    setPreview(null)
  }

  const handlePointerCancel = () => {
    cancelPreview()
  }

  return (
    <canvas
      ref={canvasRef}
      aria-label="Annotations on the frozen frame"
      className={
        pointerActive
          ? "cl:absolute cl:inset-0 cl:h-full cl:w-full cl:pointer-events-auto cl:touch-none cl:cursor-crosshair"
          : "cl:absolute cl:inset-0 cl:h-full cl:w-full cl:pointer-events-none"
      }
      role="img"
      onPointerCancel={handlePointerCancel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    />
  )
}

export default AnnotationCanvas
